import { FC, useEffect, useState } from 'react';
import { GridColDef, GridSortModel } from '@mui/x-data-grid';
import StyledGridContainer, {
  CenteredCellBox,
} from 'src/components/StyledComponents/StyledGridContainer';
import Status from 'src/components/Status';
import CustomNoRowsOverlay from 'src/components/StyledComponents/CustomEmptyTableRow';
import { useGetVoucher } from 'src/services/voucher.service';
import { Voucher, VoucherFilterValues } from 'src/models/Management.interface';
import VoucherSkeleton from 'src/components/Skeleton/VoucherSkeleton';
import StyledDataGrid from 'src/components/StyledComponents/StyledDataGrid';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import VoucherActionMenuButton from 'src/components/ActionButtons/VoucherActionMenu';
import PaginationControl from 'src/components/StyledComponents/StyledPagination';
import { SortOrder } from 'src/models/Sort.types';
import { pagination } from 'src/utils/properties.utils';

const VoucherGrid: FC<{
  state: boolean;
  searchTerm: string;
  filters: VoucherFilterValues;
}> = ({ state, searchTerm, filters }) => {
  const { showSnackbar } = StyledSnackbar();
  const [page, setPage] = useState<number>(pagination.DEFAULT_PAGE);
  const [pageSize, setPageSize] = useState<number>(
    pagination.DEFAULT_PAGE_SIZE
  );
  const { loading, vouchers, totalItems, fetchVouchers, deleteVoucher } =
    useGetVoucher();
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<SortOrder>(SortOrder.DESC);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'createdAt', sort: 'desc' },
  ]);

  useEffect(() => {
    fetchVouchers(page, pageSize, searchTerm, sortBy, order, filters);
  }, [state, filters, page, pageSize, searchTerm, sortBy, order]);

  useEffect(() => {
    setPage(1);
  }, [searchTerm, filters, state]);

  const handleSortModelChange = (model: GridSortModel) => {
    setSortModel(model);
    const sort = model[0];
    setPage(pagination.DEFAULT_PAGE);
    setSortBy(sort?.field || 'createdAt');
    setOrder((sort?.sort as SortOrder) || SortOrder.DESC);
  };

  const refetchFromFirstPage = () => {
    setPage(1);
    fetchVouchers(1, pageSize, searchTerm, sortBy, order, filters);
  };

  const columns: GridColDef[] = [
    {
      field: 'number',
      headerName: '#',
      flex: 0.15,
      headerAlign: 'center',
      align: 'center',
      sortable: false,
    },
    {
      field: 'createdAt',
      headerName: 'Created Date',
      flex: 0.7,
      renderCell: (params) =>
        params.row.createdAt
          ? new Date(params.row.createdAt).toLocaleDateString('en-GB')
          : '',
    },
    { field: 'payee', headerName: 'Payee', flex: 1 },
    { field: 'voucherType', headerName: 'Voucher type', flex: 0.9 },
    { field: 'billNumber', headerName: 'Bill Number', flex: 0.7 },
    {
      field: 'status',
      headerName: 'Status',
      flex: 0.9,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <CenteredCellBox>
          <Status status={params.value} />
        </CenteredCellBox>
      ),
    },
    {
      field: 'billAmount',
      headerName: 'Bill Amount',
      flex: 0.7,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      valueGetter: (value: number) => {
        return `${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      field: 'totalPaidAmount',
      headerName: 'Paid Amount',
      flex: 0.7,
      headerAlign: 'right',
      align: 'right',
      sortable: false,
      valueGetter: (value: number) => {
        return `${Number(value).toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`;
      },
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 0.5,
      headerAlign: 'center',
      align: 'center',
      renderCell: (params) => (
        <VoucherActionMenuButton
          rowId={String(params.id)}
          status={params.row.status}
          fetchVouchers={refetchFromFirstPage}
          deleteVoucher={deleteVoucher}
        />
      ),
    },
  ];

  const transformData = (vouchers: Voucher[]) => {
    try {
      return vouchers.map((voucher, index) => ({
        id: voucher.id,
        number: (page - 1) * pageSize + index + 1,
        createdAt: voucher.createdAt,
        payee: voucher.payee,
        voucherType: voucher.voucherType,
        billNumber: voucher.billNumber,
        billAmount: voucher.billAmount,
        totalPaidAmount: voucher.totalPaidAmount,
        status: voucher.status,
        description: voucher.description,
      }));
    } catch (error) {
      showSnackbar('Error transforming voucher data', 'error');
      return [];
    }
  };

  const rows = transformData(vouchers);

  return loading ? (
    <VoucherSkeleton columns={columns} />
  ) : (
    <StyledGridContainer
      pagination={
        totalItems > 0 ? (
          <PaginationControl
            totalItems={totalItems}
            page={page}
            setPage={setPage}
            pageSize={pageSize}
            setPageSize={setPageSize}
            loading={loading}
          />
        ) : undefined
      }
    >
      <StyledDataGrid
        rows={rows}
        columns={columns}
        sortingMode="server"
        sortModel={sortModel}
        sortingOrder={['asc', 'desc']}
        onSortModelChange={handleSortModelChange}
        disableColumnMenu
        slots={{
          noRowsOverlay: CustomNoRowsOverlay,
        }}
      />
    </StyledGridContainer>
  );
};

export default VoucherGrid;
